import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Smartphone, Monitor, Tablet, Edit2, Trash2, Send } from "lucide-react";

const iconFor = (platform, label = "") => {
  const t = (label || "").toLowerCase();
  if (t.includes("ipad") || t.includes("tablet")) return Tablet;
  if (t.includes("iphone") || t.includes("android")) return Smartphone;
  return Monitor;
};

const fmtDate = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s);
    const days = ["sön", "mån", "tis", "ons", "tor", "fre", "lör"];
    return `${days[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1} ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  } catch { return s.slice(0, 16); }
};

export function DevicesPanel() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/push/devices");
      setDevices(data.devices || []);
    } catch (e) {
      toast.error("Kunde inte hämta enheter");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveLabel = async (id) => {
    try {
      await api.patch(`/push/devices/${id}`, { label: editValue });
      toast.success("Sparat");
      setEditingId(null);
      load();
    } catch { toast.error("Kunde inte spara"); }
  };

  const removeDevice = async (id) => {
    if (!window.confirm("Ta bort denna enhet?")) return;
    try {
      await api.delete(`/push/devices/${id}`);
      toast.success("Borttagen");
      load();
    } catch { toast.error("Kunde inte ta bort"); }
  };

  const sendTestToDevice = async (id, label) => {
    try {
      const { data } = await api.post("/push/send-to-device", {
        device_id: id,
        title: "Riktad testnotis",
        body: `Hej ${label || "enhet"}! Denna notis kom bara till dig.`,
      });
      toast.success(data.ok ? "Skickat" : "Misslyckades");
    } catch { toast.error("Kunde inte skicka"); }
  };

  if (loading) return <p className="text-xs text-[color:var(--text-tertiary)]">Laddar…</p>;
  if (devices.length === 0) {
    return (
      <p className="text-xs text-[color:var(--text-tertiary)] italic">
        Inga registrerade enheter. Aktivera push så registreras denna enhet automatiskt.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="devices-list">
      {devices.map((d) => {
        const Icon = iconFor(d.platform, d.label);
        const isEditing = editingId === d.id;
        return (
          <div
            key={d.id}
            className={`flex items-center gap-3 p-3 rounded-xl border ${d.enabled ? "bg-white border-[color:var(--border)]" : "bg-[#FAFAF7] border-[color:var(--border-subtle)] opacity-60"}`}
            data-testid={`device-row-${d.id}`}
          >
            <div className="w-9 h-9 rounded-lg bg-[#EAF3EA] text-[color:var(--moss)] flex items-center justify-center flex-shrink-0">
              <Icon size={16} />
            </div>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="flex gap-2">
                  <input
                    autoFocus
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveLabel(d.id)}
                    className="flex-1 h-8 px-2 text-sm rounded border border-[color:var(--border)]"
                    data-testid={`device-label-input-${d.id}`}
                  />
                  <button
                    onClick={() => saveLabel(d.id)}
                    className="px-2 h-8 text-xs rounded bg-[color:var(--moss)] text-white"
                  >Spara</button>
                  <button onClick={() => setEditingId(null)} className="px-2 h-8 text-xs rounded">Avbryt</button>
                </div>
              ) : (
                <>
                  <p className="text-sm font-medium truncate">{d.label || "Namnlös enhet"}</p>
                  <p className="text-[11px] text-[color:var(--text-tertiary)]">
                    {d.enabled ? "Aktiv" : "Inaktiv"} · senast sedd {fmtDate(d.last_seen_at || d.updated_at)}
                  </p>
                </>
              )}
            </div>
            {!isEditing && (
              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={() => sendTestToDevice(d.id, d.label)}
                  className="w-8 h-8 rounded-lg hover:bg-[#F0EDE5] flex items-center justify-center text-[color:var(--text-tertiary)] hover:text-[color:var(--moss)]"
                  title="Skicka testnotis till denna enhet"
                  data-testid={`device-test-${d.id}`}
                >
                  <Send size={13} />
                </button>
                <button
                  onClick={() => { setEditValue(d.label || ""); setEditingId(d.id); }}
                  className="w-8 h-8 rounded-lg hover:bg-[#F0EDE5] flex items-center justify-center text-[color:var(--text-tertiary)]"
                  title="Byt namn"
                  data-testid={`device-rename-${d.id}`}
                >
                  <Edit2 size={13} />
                </button>
                <button
                  onClick={() => removeDevice(d.id)}
                  className="w-8 h-8 rounded-lg hover:bg-[#FEE2E2] flex items-center justify-center text-[color:var(--text-tertiary)] hover:text-[#D44A3A]"
                  title="Ta bort"
                  data-testid={`device-delete-${d.id}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
